import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Cake as BirthIcon,
  FavoriteBorder as MarriageIcon,
  HeartBroken as DivorceIcon,
  School as EducationIcon,
  Work as EmploymentIcon,
  Shield as MilitaryIcon,
  Flight as ImmigrationIcon,
  Home as ResidenceIcon,
  EmojiEvents as AchievementIcon,
  Event as OtherIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { TimelineEvent } from '../../types/timeline';
import { LifeEventType } from '../../types/person';

interface EventCardProps {
  event: TimelineEvent;
  onClick?: (event: TimelineEvent) => void;
}

// Custom dagger icon for death events (traditional genealogy symbol)
const DaggerIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({ fontSize = 'small' }) => {
  const size = fontSize === 'small' ? '1.25rem' : fontSize === 'large' ? '2.1875rem' : '1.5rem';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size,
        fontWeight: 'bold',
        lineHeight: 1,
      }}
    >
      †
    </Box>
  );
};

const EVENT_ICONS: Record<LifeEventType, React.ReactElement> = {
  birth: <BirthIcon fontSize="small" />,
  death: <DaggerIcon fontSize="small" />,
  marriage: <MarriageIcon fontSize="small" />,
  divorce: <DivorceIcon fontSize="small" />,
  education: <EducationIcon fontSize="small" />,
  employment: <EmploymentIcon fontSize="small" />,
  military: <MilitaryIcon fontSize="small" />,
  immigration: <ImmigrationIcon fontSize="small" />,
  residence: <ResidenceIcon fontSize="small" />,
  achievement: <AchievementIcon fontSize="small" />,
  other: <OtherIcon fontSize="small" />,
};

const EVENT_COLORS: Record<LifeEventType, string> = {
  birth: '#4caf50',
  death: '#f44336',
  marriage: '#e91e63',
  divorce: '#ff9800',
  education: '#2196f3',
  employment: '#9c27b0',
  military: '#795548',
  immigration: '#00bcd4',
  residence: '#607d8b',
  achievement: '#ffc107',
  other: '#9e9e9e',
};

/**
 * EventCard Component
 * Display a single timeline event with icon, date, location, and description
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const EventCard: React.FC<EventCardProps> = ({ event, onClick }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  const handlePersonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/people/${event.personId}`);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatLocation = (): string | null => {
    if (!event.location) return null;

    const parts = [
      event.location.city,
      event.location.state,
      event.location.country,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const eventColor = EVENT_COLORS[event.type] || EVENT_COLORS.other;

  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: onClick ? 4 : 1,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
        borderLeft: `4px solid ${eventColor}`,
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header with Icon and Type */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: `${eventColor}20`,
              color: eventColor,
              mr: 1.5,
            }}
          >
            {EVENT_ICONS[event.type]}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
            </Typography>
            {event.generation !== undefined && (
              <Typography variant="caption" color="text.secondary">
                Generation {event.generation + 1}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Person Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Tooltip title="View profile">
            <Chip
              icon={<PersonIcon />}
              label={event.personName}
              size="small"
              onClick={handlePersonClick}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>

        {/* Date */}
        {event.date && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <DateIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(event.date)}
              {event.decade && ` (${event.decade})`}
            </Typography>
          </Box>
        )}

        {/* Location */}
        {formatLocation() && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <LocationIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {formatLocation()}
            </Typography>
          </Box>
        )}

        {/* Description */}
        {event.description && (
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              mt: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {event.description}
          </Typography>
        )}

        {/* Additional metadata */}
        {(event.photoIds && event.photoIds.length > 0) && (
          <Chip
            label={`${event.photoIds.length} photo${event.photoIds.length > 1 ? 's' : ''}`}
            size="small"
            sx={{ mt: 1, fontSize: '0.7rem', height: '20px' }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default EventCard;
